import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import { ArrowUpRight, ShieldCheck, Sparkles, BadgeCheck, FileText, MapPin, Lock, Link as LinkIcon, Trash2, BellOff, CreditCard, ChevronDown, Eye, FileSearch, Crown, Wrench, BookOpen, Upload, ListChecks, Handshake, FolderArchive, DollarSign, Save, LayoutGrid, Share2 } from "lucide-react";
import { useEffect, useState } from "react";
import { fetchSampleReport, fetchArticles } from "@/lib/api";
import { SeverityBadge } from "@/components/report/SeverityBadge";
import WhyBuyersLeverageSection from "@/components/home/WhyBuyersLeverageSection";
import ExitIntentSampleModal from "@/components/home/ExitIntentSampleModal";
import ExitIntentChecklistTrigger from "@/components/home/ExitIntentChecklistTrigger";
import BuyerChecklistModal from "@/components/home/BuyerChecklistModal";
import SectionJourneyCards from "@/components/home/SectionJourneyCards";

const FAQ_ITEMS = [
    {
        q: "Is my inspection report private?",
        a: "Yes. Your report is tied to your account, stored securely, and is not publicly accessible. Only you (or someone you give a read-only share link to) can see it.",
    },
    {
        q: "Do you share my report with contractors?",
        a: "No. Quote requests are optional. We never share your full inspection report with contractors by default — only the specific scope you choose to request quotes on.",
    },
    {
        q: "Is this legal or engineering advice?",
        a: "No. Buyer's Leverage provides decision-support analysis to help you understand findings and approach negotiations. It is not legal, engineering, inspection, or contractor advice.",
    },
    {
        q: "Can I delete my report?",
        a: "Yes — anytime. Open the report → Settings → Delete. Deleted reports are removed from your dashboard immediately.",
    },
    {
        q: "Can I share it with my agent?",
        a: "Yes. We generate a secure, read-only share link that you can send to anyone — no sign-in required for them, and you can revoke the link at any time.",
    },
    {
        q: "What happens if my report fails to generate?",
        a: "Your payment is preserved. We'll retry generation without charging you again, and our support flow has a one-click 'Restore Purchase' button if anything ever locks up.",
    },
    {
        q: "Are repair exposure ranges exact quotes?",
        a: "No. They are professional-level planning ranges, localized when a ZIP code is provided. Real contractor bids will vary based on access, scope, materials, and local market.",
    },
    {
        q: "What's the difference between Inspection Summary and Closing Strategy?",
        a: "Summary helps you understand the inspection. Closing Strategy adds negotiation leverage scoring, seller-concession guidance, contingency timelines, and a strategic next-step sequence — built for buyers under contract.",
    },
];

function FaqSection() {
    const [open, setOpen] = useState(0);
    return (
        <section
            className="bl-section bg-bl-surface border-y border-bl"
            data-testid="home-faq"
        >
            <div className="bl-container max-w-3xl">
                <div className="bl-eyebrow">Common questions</div>
                <h2 data-reveal="slide-left" className="mt-4 font-display font-semibold text-3xl md:text-4xl tracking-tight text-bl leading-tight">
                    Honest answers, no fine print.
                </h2>
                <div className="mt-9 space-y-2">
                    {FAQ_ITEMS.map((it, i) => {
                        const expanded = open === i;
                        return (
                            <div
                                key={i}
                                className="bl-card overflow-hidden"
                                data-testid={`faq-item-${i}`}
                            >
                                <button
                                    type="button"
                                    onClick={() => setOpen(expanded ? -1 : i)}
                                    className="w-full text-left flex items-center justify-between gap-4 px-5 py-4 hover:bg-bl-bg/30"
                                    aria-expanded={expanded}
                                    data-testid={`faq-toggle-${i}`}
                                >
                                    <span className="font-display font-semibold text-bl">
                                        {it.q}
                                    </span>
                                    <ChevronDown
                                        className={
                                            "w-4 h-4 text-bl-muted flex-shrink-0 transition-transform " +
                                            (expanded ? "rotate-180" : "")
                                        }
                                    />
                                </button>
                                {expanded && (
                                    <div className="px-5 pb-5 text-bl-muted text-sm leading-relaxed border-t border-bl pt-3">
                                        {it.a}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

export default function Home() {
    const [sample, setSample] = useState(null);
    const [editorial, setEditorial] = useState([]);
    const [checklistOpen, setChecklistOpen] = useState(false);
    useEffect(() => {
        fetchSampleReport().then(setSample).catch(() => {});
        // 3 most recent claymation-hero articles for the editorial library tile.
        // fetchArticles() returns the unwrapped array directly.
        fetchArticles()
            .then((list) => {
                const filtered = (list || [])
                    .filter((a) => a?.hero_image?.url)
                    .slice(0, 3);
                setEditorial(filtered);
            })
            .catch(() => {});
    }, []);

    return (
        <PublicLayout>
            {/* Hero */}
            <section className="bl-paper relative overflow-hidden" data-testid="home-hero">
                <div className="bl-container pt-16 md:pt-24 pb-20 md:pb-28">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                        <div className="lg:col-span-7 min-w-0" data-reveal="slide-left-lg">
                            <div className="bl-eyebrow">
                                <BadgeCheck className="w-3.5 h-3.5" /> Inspection
                                Intelligence
                            </div>
                            <h1 className="mt-6 font-display font-semibold text-[1.75rem] xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.08] sm:leading-[1.05] lg:leading-[1.02] text-bl">
                                One home inspection report.
                                <br />
                                <span className="font-italic-accent text-bl-primary">
                                    Potentially thousands
                                </span>{" "}
                                in negotiation leverage.
                            </h1>
                            <p className="mt-6 text-base sm:text-lg md:text-xl text-bl-muted leading-relaxed max-w-xl">
                                Understand your inspection. Negotiate with confidence.
                                Comprehensive analysis designed to help buyers organize
                                concerns, reduce uncertainty, and approach negotiations
                                more strategically.
                            </p>
                            <div className="mt-9 flex flex-wrap items-center gap-3">
                                <Link
                                    to="/upload"
                                    className="bl-btn-primary"
                                    data-testid="hero-upload-cta"
                                >
                                    Upload Inspection{" "}
                                    <ArrowUpRight className="w-4 h-4" />
                                </Link>
                                <Link
                                    to="/sample-analysis"
                                    className="bl-btn-secondary"
                                    data-testid="hero-sample-cta"
                                >
                                    See a sample analysis
                                </Link>
                                <button
                                    type="button"
                                    onClick={() => setChecklistOpen(true)}
                                    className="bl-btn-secondary"
                                    data-testid="hero-checklist-cta"
                                >
                                    <FileText className="w-4 h-4" />
                                    Free inspection checklist
                                </button>
                            </div>
                            <div
                                className="mt-9 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-bl-muted"
                                data-testid="hero-trust-row"
                            >
                                {[
                                    [Lock, "Private PDF upload"],
                                    [ShieldCheck, "Secure checkout"],
                                    [LinkIcon, "Read-only sharing"],
                                    [Trash2, "Delete anytime"],
                                    [BellOff, "No contractor spam"],
                                ].map(([Ico, label], i) => (
                                    <span
                                        key={label}
                                        className="inline-flex items-center gap-1.5"
                                        data-reveal="rise-sm"
                                        data-reveal-delay={Math.min(5, i + 1)}
                                    >
                                        <Ico className="w-3.5 h-3.5 text-bl-primary" />
                                        {label}
                                    </span>
                                ))}
                            </div>
                            <p className="mt-4 text-xs text-bl-muted max-w-xl leading-relaxed">
                                Your inspection PDF is stored privately and
                                tied to your account.{" "}
                                <span className="text-bl">
                                    Quote requests are optional — your full
                                    report is never shared with contractors
                                    by default.
                                </span>
                            </p>
                        </div>

                        <div className="lg:col-span-5 min-w-0" data-reveal="slide-right-lg" data-reveal-delay="2">
                            <div
                                className="bl-card p-6 md:p-7"
                                data-testid="hero-card-preview"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="bl-eyebrow">
                                        <FileText className="w-3.5 h-3.5" /> Live
                                        preview
                                    </div>
                                    <span className="font-mono-bl text-xs text-bl-muted">
                                        Report · {sample?.issue_count || 7} issues
                                    </span>
                                </div>
                                <h2 className="mt-4 font-display text-lg md:text-xl font-semibold text-bl leading-snug">
                                    {sample?.property_address || "1428 Maple Crest Lane, Asheville, NC"}
                                </h2>
                                <div className="mt-5 space-y-3">
                                    {(sample?.preview_summary?.highlights || [
                                        { title: "Asphalt-shingle roof near end of service life", severity: "high", category: "Roofing" },
                                        { title: "Active moisture at basement rim joist", severity: "high", category: "Moisture/Water" },
                                        { title: "HVAC condenser approximately 19 years old", severity: "medium", category: "HVAC" },
                                    ])
                                        .slice(0, 3)
                                        .map((h, i) => (
                                            <div
                                                key={i}
                                                className="flex items-center justify-between gap-3 py-2 border-b border-bl last:border-0"
                                            >
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium text-bl truncate">
                                                        {h.title}
                                                    </div>
                                                    <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted mt-0.5">
                                                        {h.category}
                                                    </div>
                                                </div>
                                                <SeverityBadge severity={h.severity} />
                                            </div>
                                        ))}
                                </div>
                                <div className="mt-5 flex items-center justify-between bg-bl-teal-tint rounded-xl p-3">
                                    <div>
                                        <div className="text-[10px] tracking-[0.2em] uppercase text-bl-primary flex items-center gap-1.5">
                                            Total exposure (planning range)
                                            <span
                                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-bl-surface text-bl-muted text-[9px] tracking-[0.14em] uppercase cursor-help"
                                                title="Repair exposure is localized to the property's ZIP code. We apply the local market's labor and material multipliers, contractor minimums, and permit costs so the range reflects what an actual contractor would quote — not DIY material costs."
                                                data-testid="hero-localized-badge"
                                                tabIndex={0}
                                                aria-label="Repair exposure is localized to the property's ZIP code. We apply the local market's labor and material multipliers, contractor minimums, and permit costs."
                                            >
                                                <MapPin className="w-2.5 h-2.5" />
                                                Localized
                                            </span>
                                        </div>
                                        <div className="mt-0.5 font-mono-bl text-lg text-bl">
                                            $15,250 – $35,050
                                        </div>
                                    </div>
                                    <Link
                                        to="/sample-analysis"
                                        className="bl-btn-ghost text-sm"
                                        data-testid="hero-card-explore-link"
                                    >
                                        Explore →
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="max-w-5xl mx-auto">
                        <SectionJourneyCards
                            sectionId="hero"
                            withArrows
                        />
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="bl-section bg-bl-surface border-y border-bl" data-testid="home-how" data-reveal>
                <div className="bl-container">
                    <div className="bl-eyebrow">How it works</div>
                    <h2 data-reveal="slide-left" className="mt-4 font-display font-semibold text-3xl md:text-4xl tracking-tight text-bl">
                        From PDF to negotiation strategy in minutes.
                    </h2>

                    <div className="mt-10 grid md:grid-cols-3 gap-5">
                        {[
                            [Upload, "01", "Upload your PDF", "Drag, drop, done. We handle text-based inspection PDFs up to 25MB."],
                            [ListChecks, "02", "We organize the findings", "Issues are grouped, scored for severity, and translated into plain English."],
                            [Handshake, "03", "You negotiate from clarity", "Choose Inspection Summary or Closing Strategy. Reports stay in your dashboard."],
                        ].map(([Ico, num, title, desc], i) => (
                            <div
                                key={num}
                                className="bl-card p-7"
                                data-reveal="rise-sm"
                                data-reveal-delay={Math.min(5, i + 1)}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-[var(--bl-teal-tint)] text-bl-primary">
                                        <Ico className="h-4.5 w-4.5" strokeWidth={1.75} />
                                    </span>
                                    <span className="font-mono-bl text-bl-primary text-sm tracking-wider">
                                        {num}
                                    </span>
                                </div>
                                <h3 className="mt-4 font-display font-semibold text-xl text-bl">
                                    {title}
                                </h3>
                                <p className="mt-3 text-bl-muted leading-relaxed">
                                    {desc}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="max-w-5xl mx-auto">
                        <SectionJourneyCards sectionId="how_it_works" />
                    </div>
                </div>
            </section>

            {/* Value props */}
            <section className="bl-section bl-container" data-testid="home-values" data-reveal>
                <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5 min-w-0">
                        <div className="bl-eyebrow">What you get</div>
                        <h2 data-reveal="slide-left" className="mt-4 font-display font-semibold text-3xl md:text-4xl tracking-tight text-bl leading-tight">
                            Calm clarity, not contractor spam.
                        </h2>
                        <p className="mt-4 text-bl-muted leading-relaxed">
                            Buyer&rsquo;s Leverage helps you turn a complicated
                            inspection PDF into a clear, prioritized plan for
                            what matters most.
                        </p>
                    </div>
                    <div className="lg:col-span-7 min-w-0 grid sm:grid-cols-2 gap-4">
                        {[
                            [LayoutGrid, "Organized issue summaries", "Categorized by Roofing, Foundation, HVAC, and 14+ other systems with severity and timeline."],
                            [DollarSign, "Repair exposure ranges", "Broad planning ranges grounded in your report — not contractor bids."],
                            [Handshake, "Negotiation leverage", "Closing Strategy surfaces prioritized opportunities with suggested asks."],
                            [Save, "Saved to your dashboard", "Every paid report is securely saved. Reopen, export, or upgrade later."],
                        ].map(([Ico, t, d]) => (
                            <div key={t} className="bl-card p-6" data-reveal="rise-sm">
                                <span className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-[var(--bl-teal-tint)] text-bl-primary mb-3">
                                    <Ico className="h-4.5 w-4.5" strokeWidth={1.75} />
                                </span>
                                <div className="font-display font-semibold text-bl">
                                    {t}
                                </div>
                                <p className="mt-2 text-sm text-bl-muted leading-relaxed">
                                    {d}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Editorial library moved to bottom — see right before FAQ */}

            {/* A look inside the report — visual sample preview */}
            <section
                className="bl-section bl-container"
                data-testid="home-sample-preview"
                data-reveal
            >
                <div className="grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-5 min-w-0">
                        <div className="bl-eyebrow">
                            <Eye className="w-3.5 h-3.5" /> A look inside the report
                        </div>
                        <h2 data-reveal="slide-left" className="mt-4 font-display font-semibold text-3xl md:text-4xl tracking-tight text-bl leading-tight">
                            See exactly what{" "}
                            <span className="font-italic-accent text-bl-primary">
                                you'll get
                            </span>{" "}
                            before you buy.
                        </h2>
                        <p className="mt-4 text-bl-muted leading-relaxed">
                            We built a public sample report from a real-world
                            inspection so you can preview the structure,
                            severity tagging, exposure ranges, and negotiation
                            cues — without uploading anything first.
                        </p>
                        <ul className="mt-6 space-y-3 text-sm text-bl">
                            {[
                                ["Categorized issue cards", "Roofing, foundation, HVAC, plumbing, and 14+ systems — each with severity and timeline."],
                                ["Estimated Professional Repair Exposure", "Planning ranges grounded in scope + ZIP, not contractor bids."],
                                ["Closing Strategy intelligence", "Leverage scoring, seller-concession guidance, sequenced action roadmap (Premium)."],
                            ].map(([t, d]) => (
                                <li key={t} className="flex gap-3">
                                    <BadgeCheck className="w-4 h-4 text-bl-primary mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="font-display font-semibold text-bl">{t}</div>
                                        <div className="text-bl-muted leading-relaxed">{d}</div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link
                                to="/sample-analysis"
                                className="bl-btn-primary"
                                data-testid="home-sample-preview-cta"
                            >
                                See the full sample →
                            </Link>
                            <Link
                                to="/upload"
                                className="bl-btn-secondary"
                                data-testid="home-sample-preview-upload-cta"
                            >
                                Upload mine instead
                            </Link>
                        </div>
                    </div>

                    <div className="lg:col-span-7 min-w-0">
                        <div
                            className="bl-card p-5 md:p-6 relative"
                            data-testid="home-sample-thumbnail"
                        >
                            {/* Stylised report thumbnail header */}
                            <div className="flex items-center justify-between pb-4 border-b border-bl">
                                <div>
                                    <div className="bl-eyebrow">
                                        <FileSearch className="w-3.5 h-3.5" /> Inspection
                                        Summary
                                    </div>
                                    <div className="mt-2 font-display font-semibold text-bl text-base md:text-lg">
                                        1428 Maple Crest Lane, Asheville, NC
                                    </div>
                                </div>
                                <span
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-bl-teal-tint text-bl-primary text-[10px] tracking-[0.14em] uppercase"
                                >
                                    <MapPin className="w-2.5 h-2.5" />
                                    Localized · 28801
                                </span>
                            </div>

                            {/* Issue rows — richer, 4 issues to fill the preview */}
                            <div className="mt-4 space-y-3">
                                {[
                                    ["Asphalt-shingle roof near end of service life", "Roofing", "high", "$11,800 – $18,200"],
                                    ["Active moisture at basement rim joist", "Moisture / Water", "high", "$2,100 – $6,400"],
                                    ["HVAC condenser ~19 years old", "HVAC", "medium", "$5,400 – $8,900"],
                                    ["Outdated double-tapped breakers in panel", "Electrical", "low", "$340 – $720"],
                                ].map(([title, cat, sev, range]) => (
                                    <div
                                        key={title}
                                        className="flex items-start justify-between gap-3 py-3 border-b border-bl last:border-0"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium text-bl">
                                                {title}
                                            </div>
                                            <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted mt-0.5">
                                                {cat}
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                            <SeverityBadge severity={sev} />
                                            <div className="font-mono-bl text-xs text-bl">
                                                {range}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Closing strategy strip — Premium teaser */}
                            <div className="mt-4 rounded-xl bg-bl-bg/40 border border-bl p-4 flex items-start gap-3">
                                <Crown className="w-4 h-4 text-bl-primary flex-shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                    <div className="text-[10px] tracking-[0.18em] uppercase text-bl-primary">
                                        Closing Strategy · Premium
                                    </div>
                                    <div className="mt-1 text-sm text-bl leading-relaxed">
                                        Leverage scoring, seller-concession
                                        strategy, sequenced action roadmap, and
                                        contractor questions — built for buyers
                                        under contract.
                                    </div>
                                </div>
                            </div>

                            {/* Footer totals row */}
                            <div className="mt-4 flex items-center justify-between bg-bl-teal-tint rounded-xl p-4">
                                <div>
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-bl-primary">
                                        Total planning exposure
                                    </div>
                                    <div className="mt-1 font-mono-bl text-xl text-bl">
                                        $19,640 – $34,220
                                    </div>
                                </div>
                                <Link
                                    to="/sample-analysis"
                                    className="bl-btn-ghost text-sm"
                                    data-testid="home-sample-thumbnail-link"
                                >
                                    Open full sample →
                                </Link>
                            </div>

                            <p className="mt-3 text-[10px] tracking-[0.16em] uppercase text-bl-muted">
                                Visual preview · Numbers reflect the public
                                sample property
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* What happens to your report? — privacy / process clarity */}
            <section
                className="bl-section bg-bl-surface border-y border-bl"
                data-testid="home-what-happens"
                data-reveal
            >
                <div className="bl-container">
                    <div className="bl-eyebrow">What happens to your report?</div>
                    <h2 data-reveal="slide-left" className="mt-4 font-display font-semibold text-3xl md:text-4xl tracking-tight text-bl leading-tight max-w-2xl">
                        You stay{" "}
                        <span className="font-italic-accent text-bl-primary">
                            in control
                        </span>{" "}
                        — from upload to share to delete.
                    </h2>
                    <p className="mt-4 text-bl-muted leading-relaxed max-w-2xl">
                        Your inspection report stays tied to your account.
                        You decide whether to download it, share a read-only
                        link, request quotes, or delete it later.
                    </p>
                    <div className="mt-9 grid md:grid-cols-3 gap-5">
                        {[
                            [Upload, "01", "Upload your inspection PDF", "Private upload — your PDF is stored securely and tied to your account."],
                            [FolderArchive, "02", "We organize the findings", "Issues are categorized, scored, and translated into plain English."],
                            [Share2, "03", "You choose what's next", "Download, share a read-only link, request quotes, or delete it. Anytime."],
                        ].map(([Ico, num, title, desc], i) => (
                            <div
                                key={num}
                                className="bl-card p-7"
                                data-reveal="rise-sm"
                                data-reveal-delay={Math.min(5, i + 1)}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-[var(--bl-teal-tint)] text-bl-primary">
                                        <Ico className="h-4.5 w-4.5" strokeWidth={1.75} />
                                    </span>
                                    <span className="font-mono-bl text-bl-primary text-sm tracking-wider">
                                        {num}
                                    </span>
                                </div>
                                <h3 className="mt-4 font-display font-semibold text-xl text-bl">
                                    {title}
                                </h3>
                                <p className="mt-3 text-bl-muted leading-relaxed">
                                    {desc}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="max-w-5xl mx-auto">
                        <SectionJourneyCards sectionId="what_happens" />
                    </div>
                </div>
            </section>

            {/* Built for private, buyer-controlled inspection analysis */}
            <section
                className="bl-section bl-container"
                data-testid="home-privacy-trust"
                data-reveal
            >
                <div className="bl-eyebrow">Privacy by design</div>
                <h2 data-reveal="slide-left" className="mt-4 font-display font-semibold text-3xl md:text-4xl tracking-tight text-bl leading-tight max-w-3xl">
                    Built for private, buyer-controlled inspection analysis.
                </h2>
                <div className="mt-9 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        [Lock, "Private by default", "Your uploaded inspection PDF is tied to your account and not publicly accessible."],
                        [LinkIcon, "You control sharing", "Generate a secure, read-only link whenever you want to share — never automatic."],
                        [Trash2, "Delete saved reports", "Remove reports from your dashboard the moment you no longer need them."],
                        [BellOff, "No contractor spam", "Quote requests are optional. Your full report is not shared with contractors by default."],
                    ].map(([Ico, title, desc], i) => (
                        <div
                            key={title}
                            className="bl-card p-6"
                            data-reveal="rise-sm"
                            data-reveal-delay={Math.min(5, i + 1)}
                        >
                            <Ico className="w-5 h-5 text-bl-primary" />
                            <div className="mt-4 font-display font-semibold text-bl">
                                {title}
                            </div>
                            <p className="mt-2 text-sm text-bl-muted leading-relaxed">
                                {desc}
                            </p>
                        </div>
                    ))}
                </div>
                <p className="mt-8 text-xs text-bl-muted max-w-3xl leading-relaxed">
                    Buyer's Leverage provides decision-support analysis, not
                    legal, engineering, inspection, or contractor advice.
                    Repair exposure ranges are planning estimates, not bids.
                </p>
            </section>

            {/* Why buyers choose Buyer's Leverage — confidence row */}
            <section
                className="bl-section bg-bl-surface border-y border-bl"
                data-testid="home-why-buyers-choose"
                data-reveal
            >
                <div className="bl-container">
                    <div className="bl-eyebrow">
                        <BadgeCheck className="w-3.5 h-3.5" /> Why buyers choose it
                    </div>
                    <h2 data-reveal="slide-left" className="mt-4 font-display font-semibold text-3xl md:text-4xl tracking-tight text-bl leading-tight max-w-3xl">
                        Calm, organized, and{" "}
                        <span className="font-italic-accent text-bl-primary">
                            on your side
                        </span>{" "}
                        — not the contractor's.
                    </h2>
                    <p className="mt-4 text-bl-muted leading-relaxed max-w-2xl">
                        Buyer's Leverage is built for the homebuyer reading a
                        20–80 page inspection PDF at 11pm — not for partners,
                        contractors, or ad networks.
                    </p>
                    <div className="mt-9 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                            [Wrench, "Built for buyers — not contractors", "Every line of the report is written for someone deciding whether to negotiate, walk, or buy. No upsells to repair services."],
                            [MapPin, "Localized exposure ranges, not bids", "We apply local labor + material multipliers, contractor minimums, and permit costs — so the range reflects what a real contractor would quote, not DIY costs."],
                            [Lock, "Your inspection stays yours", "Your PDF is stored privately, deletable any time, and never shared with contractors by default. Quote requests are always opt-in."],
                            [CreditCard, "Pay once. Re-open anytime.", "One-time reports stay saved in your dashboard. No hidden subscription, no scarcity timers, no re-purchase if you upgrade."],
                        ].map(([Ico, title, desc], i) => (
                            <div
                                key={title}
                                className="bl-card p-6"
                                data-reveal="rise-sm"
                                data-reveal-delay={Math.min(5, i + 1)}
                            >
                                <Ico className="w-5 h-5 text-bl-primary" />
                                <div className="mt-4 font-display font-semibold text-bl">
                                    {title}
                                </div>
                                <p className="mt-2 text-sm text-bl-muted leading-relaxed">
                                    {desc}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="max-w-5xl mx-auto">
                        <SectionJourneyCards sectionId="why_choose" />
                    </div>
                </div>
            </section>

            {/* Objection handling — why purpose-built beats a general chatbot */}
            <WhyBuyersLeverageSection />

            {/* Homepage FAQ — objection handling */}
            <FaqSection />

            {/* Editorial library — claymation-hero teaser linking out to /learn.
                3 most recent articles fetched on mount; falls back gracefully
                if the API returns nothing. Placed after the FAQ so readers
                land on related content right before the final CTA. */}
            {editorial.length > 0 && (
                <section
                    className="bl-section bg-bl-surface border-y border-bl"
                    data-testid="home-editorial-library"
                    data-reveal
                >
                    <div className="bl-container">
                        <div className="flex items-end justify-between gap-6 flex-wrap">
                            <div>
                                <div className="bl-eyebrow">
                                    <BookOpen className="w-3.5 h-3.5" /> Editorial library
                                </div>
                                <h2 data-reveal="slide-left" className="mt-4 font-display font-semibold text-3xl md:text-4xl tracking-tight text-bl leading-tight">
                                    Calm reads on{" "}
                                    <span className="font-italic-accent text-bl-primary">
                                        real inspection findings.
                                    </span>
                                </h2>
                                <p className="mt-3 text-bl-muted leading-relaxed max-w-xl">
                                    Honest, claymation-illustrated guides on what your
                                    inspector flagged, what it costs to fix, and what's
                                    actually worth negotiating.
                                </p>
                            </div>
                            <Link
                                to="/learn"
                                className="bl-btn-ghost text-sm gap-1.5"
                                data-testid="home-editorial-browse-all"
                            >
                                Browse all guides
                                <ArrowUpRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>

                        <div className="mt-10 grid md:grid-cols-3 gap-5">
                            {editorial.map((a, i) => (
                                <Link
                                    key={a.slug}
                                    to={`/learn/${a.slug}`}
                                    className="bl-card bl-card-hover block overflow-hidden"
                                    data-testid={`home-editorial-card-${a.slug}`}
                                    data-reveal="rise-sm"
                                    data-reveal-delay={Math.min(5, i + 1)}
                                >
                                    <div className="aspect-[16/9] bg-bl-cream overflow-hidden">
                                        <img
                                            src={`${process.env.REACT_APP_BACKEND_URL}${a.hero_image.url}?w=600`}
                                            alt={a.featured_image_alt || a.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="p-6">
                                        <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                                            {a.category}
                                        </div>
                                        <h3 className="mt-2 font-display font-semibold text-lg text-bl leading-snug">
                                            {a.title}
                                        </h3>
                                        <p className="mt-2 text-sm text-bl-muted leading-relaxed line-clamp-3">
                                            {a.excerpt}
                                        </p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* CTA */}
            <section className="bl-section bl-container" data-testid="home-cta" data-reveal>
                <div className="bl-card p-8 md:p-14 relative overflow-hidden">
                    <div className="max-w-2xl">
                        <h2 data-reveal="slide-left" className="font-display font-semibold text-[1.75rem] sm:text-3xl md:text-5xl tracking-tight text-bl leading-tight">
                            Ready to{" "}
                            <span className="font-italic-accent text-bl-primary">
                                understand
                            </span>{" "}
                            what's actually in your inspection?
                        </h2>
                        <p className="mt-5 text-bl-muted text-base sm:text-lg max-w-xl">
                            One upload. A clear, prioritized view of the home. A more
                            confident negotiation.
                        </p>
                        <div className="mt-7 flex flex-wrap gap-3">
                            <Link
                                to="/upload"
                                className="bl-btn-primary"
                                data-testid="footer-cta-upload"
                            >
                                Upload Inspection
                            </Link>
                            <Link
                                to="/pricing"
                                className="bl-btn-secondary"
                                data-testid="footer-cta-pricing"
                            >
                                See pricing
                            </Link>
                        </div>
                    </div>

                    <div className="mt-10 max-w-4xl mx-auto">
                        <SectionJourneyCards sectionId="cta" />
                    </div>
                </div>
            </section>

            {/* Quiet exit-intent overlay — only triggers when an engaged visitor
                aims their cursor at the tab bar. 50/50 A/B between Sample preview
                (existing) and Checklist PDF (Cohort 1 v4.77 variant). Mutually
                exclusive per session via `bl_exit_intent_variant`. */}
            <ExitIntentSampleModal source="home" />
            <ExitIntentChecklistTrigger source="home" />

            {/* Cohort 1 lead magnet — email-gated checklist PDF. Summoned by
                the "Free inspection checklist" secondary CTA in the hero. */}
            <BuyerChecklistModal
                open={checklistOpen}
                onOpenChange={setChecklistOpen}
                source="home_hero"
            />
        </PublicLayout>
    );
}
